import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { api } from "api";
import { DATE_FORMAT } from "constants/dateFormat";
import { useFetchStateHelper } from "hooks/useFetchState";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

const STALE_TIME = 1000 * 60 * 10; // 10 minutes

const getDateSpanBasedOnGivenDate = (date) => {
  const firstOfYear = moment(date).startOf("year").format(DATE_FORMAT);
  const firstOfFirstWeek = moment(firstOfYear)
    .startOf("week")
    .format(DATE_FORMAT);
  const endOfYear = moment(date).endOf("year").format(DATE_FORMAT);
  const endOfLastWeek = moment(endOfYear).endOf("week").format(DATE_FORMAT);

  const startDate = firstOfFirstWeek;
  const endDate = moment().isBefore(endOfLastWeek)
    ? moment().format(DATE_FORMAT)
    : endOfLastWeek;

  return {
    startDate,
    endDate,
  };
};

export const useIndices = ({ fieldId, date }) => {
  const [searchParams] = useSearchParams();
  const currentDate = searchParams.get("date");

  const dateToFetch = date || currentDate || moment().format(DATE_FORMAT);

  const { startDate, endDate } = getDateSpanBasedOnGivenDate(dateToFetch);

  const { data: yearlyIndices, ...restResponse } = useQuery({
    queryFn: () =>
      api.satellite.fetchMany({
        fieldId,
        startDate,
        endDate,
      }),
    queryKey: ["yearly-indices", fieldId, startDate, endDate],
    staleTime: STALE_TIME,
  });

  const { fetchState } = useFetchStateHelper(restResponse);

  const isCurrentDateIndexed = useMemo(
    () => yearlyIndices?.availableDates?.includes(currentDate),
    [yearlyIndices, currentDate]
  );

  const checkIfDateHasIndex = useCallback(
    (date) => {
      return yearlyIndices?.availableDates?.includes(date);
    },
    [yearlyIndices]
  );

  const getIndexOfDate = useCallback(
    (date) => {
      return yearlyIndices?.indices?.[date];
    },
    [yearlyIndices]
  );

  return {
    availableDates: yearlyIndices?.availableDates,
    indices: yearlyIndices?.indices,
    isCurrentDateIndexed,
    fetchState,
    checkIfDateHasIndex,
    getIndexOfDate,
  };
};

export const useIndexImage = ({ fieldId, index, date, hasIndex }) => {
  const { data: image, ...restResponse } = useQuery({
    queryFn: () => api.satellite.fetchImage({ fieldId, index, date }),
    queryKey: ["daily-index-image", fieldId, date, index],
    staleTime: STALE_TIME,
    enabled: hasIndex,
  });

  const { fetchState } = useFetchStateHelper(restResponse);

  return { image, fetchState };
};
